import { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  LayoutGrid,
  Building2,
  Star,
  Briefcase,
  GraduationCap,
  Inbox,
  LogOut,
  ExternalLink,
  Radio,
  KeyRound,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const NAV = [
  { to: "/admin/profile", label: "Profile", icon: LayoutGrid, testId: "admin-nav-profile" },
  { to: "/admin/companies", label: "Companies", icon: Building2, testId: "admin-nav-companies" },
  { to: "/admin/featured", label: "Featured", icon: Star, testId: "admin-nav-featured" },
  { to: "/admin/portfolio", label: "Portfolio", icon: Briefcase, testId: "admin-nav-portfolio" },
  { to: "/admin/experience", label: "Experience", icon: GraduationCap, testId: "admin-nav-experience" },
  { to: "/admin/messages", label: "Messages", icon: Inbox, testId: "admin-nav-messages" },
  { to: "/admin/keys", label: "API Keys", icon: KeyRound, testId: "admin-nav-keys" },
  { to: "/admin/social", label: "Social Operator", icon: Radio, testId: "admin-nav-social" },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const handleLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background relative" data-testid="admin-layout">
      <div className="grid-overlay" />
      <div className="relative flex flex-col lg:flex-row min-h-screen">
        {/* Sidebar */}
        <aside className="w-full lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-border bg-card/40 backdrop-blur">
          <div className="p-5 border-b border-border">
            <span className="mono-label text-primary text-[10px]">Admin Console</span>
            <h1 className="text-base font-semibold mt-1" style={{ fontFamily: "var(--font-display)" }}>
              Tylegn Studio
            </h1>
            <p className="text-xs text-muted-foreground mt-1 truncate">{admin?.email}</p>
          </div>

          <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible" aria-label="Admin sections">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-primary/15 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`
                }
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="p-3 lg:absolute lg:bottom-0 lg:left-0 lg:right-0 lg:w-64 space-y-2 border-t border-border lg:border-t">
            <Button
              asChild
              variant="secondary"
              size="sm"
              className="w-full justify-start"
              data-testid="admin-view-site-button"
            >
              <a href="/" target="_blank" rel="noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" /> View site
              </a>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
